import { createContext, Dispatch, SetStateAction, useEffect, useState } from 'react'
import qs from 'qs'
import { ID, QueryResponseContextProps, QueryState } from './models'
import { useLocation } from 'react-router-dom'

function createResponseContext<T>(initialState: QueryResponseContextProps<T>) {
  return createContext(initialState)
}

function isNotEmpty(obj: unknown) {
  return obj !== undefined && obj !== null && obj !== ''
}

// Example: page=1&size=10&sort=id&order=desc&search=a&filter_name=a&filter_online=false
function stringifyRequestQuery(data: QueryState): string {
  const state = {
    ...data,
    [data.initialApi?.includes('remark') ? 'startDate' : 'start_date']:
      data.start_date !== null && typeof data.start_date === 'object'
        ? new Date(data.start_date).toLocaleDateString('en-US')
        : data.initialApi?.includes('remark')
          ? new Date(data.start_date).getTime()
          : data.start_date,
    [data.initialApi?.includes('remark') ? 'endDate' : 'end_date']:
      data.end_date !== null && typeof data.end_date === 'object'
        ? new Date(data.end_date).toLocaleDateString('en-US')
        : data.initialApi?.includes('remark')
          ? new Date(data.end_date).getTime()
          : data.end_date,
  }
  const pagination = qs.stringify(state, {
    filter: [
      'page',
      'size',
      'type',
      'facilityType',
      'status',
      ...((data?.initialApi?.includes('remark') && []) || ['start_date']),
      ...((data?.initialApi?.includes('remark') && []) || ['end_date']),
      'startDate',
      // 'start_date',
      // 'end_date',
      'endDate',
      'franchiseeid',
      'categoryId',
      'customerId',
      'customerSort',
    ],
    skipNulls: true,
  })
  const sort = qs.stringify(state, { filter: ['sort', 'order'], skipNulls: true })
  const search = isNotEmpty(state.search)
    ? qs.stringify(state, { filter: ['search'], skipNulls: true })
    : ''

  const filter = state.filter
    ? Object.entries(state.filter as Object)
      .filter((obj) => isNotEmpty(obj[1]))
      .map((obj) => {
        return `filter_${obj[0]}=${obj[1]}`
      })
      .join('&')
    : ''

  return [pagination, sort, search, filter].filter((f) => f).join('&')
}

function parseRequestQuery(query: string): QueryState {
  const cache: unknown = qs.parse(query)
  return cache as QueryState
}

function calculatedGroupingIsDisabled<T>(isLoading: boolean, data: Array<T> | undefined): boolean {
  if (isLoading) {
    return true
  }

  return !data || !data.length
}

function calculateIsAllDataSelected<T>(data: Array<T> | undefined, selected: Array<ID>): boolean {
  if (!data) {
    return false
  }

  return data.length > 0 && data.length === selected.length
}

function groupingOnSelect(
  id: ID,
  selected: Array<ID>,
  setSelected: Dispatch<SetStateAction<Array<ID>>>
) {
  if (!id) {
    return
  }

  if (selected.includes(id)) {
    setSelected(selected.filter((itemId) => itemId !== id))
  } else {
    const updatedSelected = [...selected]
    updatedSelected.push(id)
    setSelected(updatedSelected)
  }
}

function groupingOnSelectAll<T>(
  isAllSelected: boolean,
  setSelected: Dispatch<SetStateAction<Array<ID>>>,
  data?: Array<T & { productionId?: ID }>
) {
  if (isAllSelected) {
    setSelected([])
    return
  }

  if (!data || !data.length) {
    return
  }
  setSelected(data.filter((item) => item.productionId).map((item) => item.productionId))
}

// Hook
function useDebounce(value: string | undefined, delay: number) {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value)
  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value)
      }, delay)
      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler)
      }
    },
    [value, delay] // Only re-call effect if value or delay changes
  )
  return debouncedValue
}

function capitalizeWords(string) {
  return string.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\b\w/g, (char) => char.toUpperCase())
}

function makeSelectDropDown(format = '', options: any = [], disabled = false, formik: any) {
  return (
    <select style={{
      backgroundColor: '#ffffff',
      border: '1px solid #dddddd',
      color: '#000000', // Text color for selected option
    }}
      className='form-select form-select-solid ' {...formik.getFieldProps(format)}>
      <option value=''>Select one...</option>
      {options.map((eachOption, eachInd) => (
        <option disabled={disabled} key={eachInd + 1 + ''} value={eachOption.value}>
          {eachOption.label}
        </option>
      ))}
    </select>
  )
}

export {
  createResponseContext,
  stringifyRequestQuery,
  parseRequestQuery,
  calculatedGroupingIsDisabled,
  calculateIsAllDataSelected,
  groupingOnSelect,
  groupingOnSelectAll,
  useDebounce,
  isNotEmpty,
  capitalizeWords,
  makeSelectDropDown,
}
